import { faCircleQuestion, faCirclePlay } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { UserCarouselSlug } from 'api/userCarousels/types'
import { DropdownMenuContent, DropdownMenuItem } from 'components/DropdownMenu'
import { UserCarousel } from 'components/UserCarousel'
import { useHasAdminAccess } from 'hooks/useHasAdminAccess'
import { useTriggerEvent } from 'hooks/useTriggerEvent'
import { getConfiguration } from 'services/config'
import { useTranslation } from 'services/i18n'

import { UserCarouselButton } from './UserCarouselButton'

import type { FC } from 'react'

type Props = {
  onToggleHelpAndFeedbackMenu: VoidFunction
}

export const HelpAndFeedbackMenuContent: FC<Props> = ({
  onToggleHelpAndFeedbackMenu,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'help_and_feedback',
  })

  const hasAdminAccess = useHasAdminAccess()

  const { tutorialUrl } = getConfiguration()

  const triggerEvent = useTriggerEvent()

  const [activeUserCarousel, setActiveUserCarousel]
    = useState<UserCarouselSlug | null>(null)

  const handleTriggerEvent = useCallback(
    (menuItemName: string) =>
      triggerEvent({
        name: 'kai_help_menu_item_clicked',
        properties: {
          menuItemName,
        },
      }),
    [triggerEvent],
  )

  const handleClickHelp = useCallback(() => {
    onToggleHelpAndFeedbackMenu()

    handleTriggerEvent('help')
  }, [handleTriggerEvent, onToggleHelpAndFeedbackMenu])

  const handleClickUserCarousel = useCallback(
    (carouselSlug: UserCarouselSlug) => {
      onToggleHelpAndFeedbackMenu()

      handleTriggerEvent(`user_carousel_${carouselSlug}`)

      setActiveUserCarousel(carouselSlug)
    },
    [handleTriggerEvent, onToggleHelpAndFeedbackMenu],
  )

  const handleDismissCarousel = useCallback(
    () => setActiveUserCarousel(null),
    [],
  )

  return (
    <>
      {activeUserCarousel && (
        <UserCarousel
          carouselSlug={activeUserCarousel}
          onDismissCarousel={handleDismissCarousel}
        />
      )}
      <DropdownMenuContent
        className="mx-6 mb-2 flex max-w-80 flex-col"
        onEscapeKeyDown={onToggleHelpAndFeedbackMenu}
        onPointerDownOutside={onToggleHelpAndFeedbackMenu}
      >
        <DropdownMenuItem className="h-10">
          <Link
            aria-label={t('dropdown_options.help')}
            className="text-sm text-default"
            onClick={handleClickHelp}
            to="help"
          >
            <FontAwesomeIcon icon={faCircleQuestion} className="mr-2 w-4" />
            {t('dropdown_options.help')}
          </Link>
        </DropdownMenuItem>
        {tutorialUrl && (
          <DropdownMenuItem className="h-10">
            <Link
              aria-label={t('dropdown_options.tutorial')}
              className="text-sm text-default"
              to={tutorialUrl}
            >
              <FontAwesomeIcon icon={faCirclePlay} className="mr-2 w-4" />
              {t('dropdown_options.tutorial')}
            </Link>
          </DropdownMenuItem>
        )}
        {hasAdminAccess && (
          <DropdownMenuItem className="h-10">
            <UserCarouselButton
              carouselSlug="onboarding"
              onClick={handleClickUserCarousel}
            />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </>
  )
}
